<!-- Port of vendor-ref/mara/app/ui/src/components/MessageInput.vue (contenteditable field, actions and send slots). -->
<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Button from 'primevue/button';

const props = withDefaults(defineProps<{ placeholder?: string; editable?: boolean; sendOnEnter?: boolean }>(), {
  placeholder: '', editable: true, sendOnEnter: true,
});
const emit = defineEmits<{ send: [text: string] }>();
const message = defineModel<string>({ default: '' });

const el = ref<HTMLElement | null>(null);

watch(message, () => {
  if (el.value && el.value.innerText.trim() !== message.value) el.value.innerText = message.value;
});

const sendEnabled = computed(() => !!message.value.trim());

function onInput(e: Event) { message.value = (e.target as HTMLElement).innerText.trim(); }

function onEnter(e: KeyboardEvent) {
  if (!props.sendOnEnter || e.shiftKey || e.altKey || e.isComposing) return;
  e.preventDefault();
  send();
}

function send() {
  if (!sendEnabled.value) return;
  emit('send', message.value.trim());
}

function clear() {
  message.value = '';
  if (el.value) el.value.innerText = '';
}

function focus() { el.value?.focus(); }

defineExpose({ el, focus, clear });
</script>

<template>
  <div
    class="relative w-full p-2 flex flex-wrap justify-end bg-section shrink-0"
    style="border-radius: calc(var(--button-icon-only-width) / 2 + 0.5em)"
  >
    <div class="max-w-full grow-1 p-2 pl-4 of-y-auto" style="max-height: calc(100dvh - 25rem)">
      <div
        ref="el"
        :class="{ 'is-empty': !message }"
        :contenteditable="editable"
        :placeholder="placeholder"
        class="message-input-field relative"
        role="textbox"
        aria-label="Message MARA"
        data-test="chat-input-field"
        @input="onInput"
        @keydown.enter="onEnter"
      />
    </div>

    <div class="flex items-end gap-1">
      <slot />
      <slot name="send">
        <Button :disabled="!sendEnabled" class="ml-1" icon="i-tabler-arrow-up" rounded @click="send" />
      </slot>
    </div>
  </div>
</template>

<style>
.message-input-field {
  outline: none !important;
}
.message-input-field.is-empty:before {
  content: attr(placeholder);
  position: absolute;
  color: var(--form-field-placeholder-color);
  pointer-events: none;
}
</style>
